import React from 'react';
import { 
  FaFileInvoice, FaCloud, FaUpload, FaArrowRight, 
  FaCheckCircle, FaLock, FaEye, FaRocket, FaRobot, FaMobileAlt
} from 'react-icons/fa';
import './TallyIra.css';

const TallyIra = () => {
  return (
    <div className="tally-ira-wrapper">
      <header className="ira-hero">
        <div className="ira-hero-overlay"></div>
        <div className="ira-hero-container">
          <div className="ira-hero-content">
            <div className="ira-badge">
              <FaRobot className="ira-badge-icon" />
              <span>AI-POWERED AUTOMATION</span>
            </div>
            <h1>TallyPrime with TallyIra</h1>
            <p>
              Experience the future of accounting. Scan physical bills, upload invoices, and automatically 
              generate verified draft entries directly inside TallyPrime.
            </p>
            <div className="ira-hero-stats">
              <div className="stat-card">
                <h3>80%</h3>
                <p>Reduction in Manual Entry</p>
              </div>
              <div className="stat-card">
                <h3>99.9%</h3>
                <p>Data Extraction Accuracy</p>
              </div>
              <div className="stat-card">
                <h3>1-Click</h3>
                <p>Secure Document Upload</p>
              </div>
            </div>
          </div>
          <div className="ira-hero-visual-side">
            <img src="/images/tally-ira/tally-ira-element.svg" alt="TallyPrime Ira AI Assistant" className="ira-hero-svg-img" />
          </div>
        </div>
      </header>

      {/* ── Core Services Split Section ── */}
      <section className="ira-sections-container">
        
        {/* Docs by Ira */}
        <div className="ira-feature-block">
          <div className="ira-feature-text">
            <div className="feature-icon-title">
              <FaFileInvoice className="feature-title-icon" />
              <h2>Docs by Ira (AI Invoice Processing)</h2>
            </div>
            <p className="feature-intro">
              Transform physical and digital invoices into ready-to-post TallyPrime transactions. 
              Powered by advanced optical character recognition (OCR) and machine learning, Docs by Ira 
              reads documents dynamically without requiring fixed templates.
            </p>
            
            <div className="workflow-steps">
              <div className="step">
                <div className="step-num">1</div>
                <div>
                  <h4>Flexible Ingestion</h4>
                  <p>Upload invoices via WhatsApp, Email, Desktop App, or scan directly through the Mobile App.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-num">2</div>
                <div>
                  <h4>Intelligent Extraction</h4>
                  <p>AI automatically parses vendor names, dates, invoice numbers, amounts, and detailed GST rates.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-num">3</div>
                <div>
                  <h4>Review & Post</h4>
                  <p>Verify extracted details side-by-side with the uploaded document before approving transaction entry.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="ira-feature-visual">
            <div className="visual-card upload-animation-card">
              <div className="card-header-bar">
                <span className="dot red"></span>
                <span className="dot yellow"></span>
                <span className="dot green"></span>
                <span className="window-title">AI Processing Pipeline</span>
              </div>
              <div className="pipeline-container">
                <div className="pipeline-node node-upload">
                  <FaUpload className="node-icon" />
                  <span>Invoice Upload</span>
                </div>
                <FaArrowRight className="pipeline-arrow" />
                <div className="pipeline-node node-ai">
                  <FaRobot className="node-icon pulse" />
                  <span>Ira AI Engine</span>
                </div>
                <FaArrowRight className="pipeline-arrow" />
                <div className="pipeline-node node-tally">
                  <FaCheckCircle className="node-icon success" />
                  <span>Tally Draft Voucher</span>
                </div>
              </div>
              <div className="pipeline-details-box">
                <div className="detail-row">
                  <span className="lbl">Extracted Vendor:</span>
                  <span className="val">iMatrix Technologies</span>
                </div>
                <div className="detail-row">
                  <span className="lbl">Tax Details:</span>
                  <span className="val">CGST & SGST 9% (Identified)</span>
                </div>
                <div className="detail-row">
                  <span className="lbl">Confidence Score:</span>
                  <span className="val green-text">99.8% Perfect Match</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Secure Document Scan & Upload */}
        <div className="ira-feature-block reverse">
          <div className="ira-feature-visual">
            <div className="visual-card upload-features-card">
              <div className="card-header-bar">
                <span className="dot red"></span>
                <span className="dot yellow"></span>
                <span className="dot green"></span>
                <span className="window-title">Supported Formats</span>
              </div>
              <div className="preview-reports-list" style={{padding: '30px 24px'}}>
                <div className="report-item"><FaCheckCircle /> <span>PDF Invoices & Bills</span></div>
                <div className="report-item"><FaCheckCircle /> <span>Scanned Images (JPG / PNG)</span></div>
                {/* <div className="report-item"><FaCheckCircle /> <span>Multi-page TIFF Documents</span></div> */}
                <div className="report-item"><FaCheckCircle /> <span>Bulk ZIP File Uploads</span></div>
              </div>
            </div>
          </div>

          <div className="ira-feature-text">
            <div className="feature-icon-title">
              <FaCloud className="feature-title-icon" />
              <h2>Secure Document Scan & Upload</h2>
            </div>
            <p className="feature-intro">
              Instantly digitize your physical bills. Scan receipts with your mobile camera or upload electronic files. 
              TallyIra matches and stores each document directly alongside your active TallyPrime ledger accounts.
            </p>
            <ul className="benefits-checklist">
              <li>
                <FaLock className="benefit-icon" />
                <div>
                  <strong>Safe & Encrypted Ingestion</strong>
                  <span>Your financial data is encrypted in transit using SSL certificates, keeping business information secure.</span>
                </div>
              </li>
              <li>
                <FaEye className="benefit-icon" />
                <div>
                  <strong>Visual Side-by-Side Review</strong>
                  <span>Verify document contents alongside draft transactions inside your desktop application before committing entries.</span>
                </div>
              </li>
              <li>
                <FaMobileAlt className="benefit-icon" />
                <div>
                  <strong>Seamless Mobile Scanner</strong>
                  <span>Capture document frames automatically, adjust crop borders, and upload files to the processing stack in one tap.</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

      </section>

      {/* ── Video Demo Section ── */}
      <section className="ira-video-split-section">
        <div className="ira-video-split-container">
          <div className="ira-video-left">
            <div className="ira-video-wrapper">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="ira-feature-video"
              >
                <source src="/images/tally-ira/tally-ira-screen-phone.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
          <div className="ira-video-right-text">
            <span className="ira-eyebrow">interactive companion demo</span>
            <h2>TallyPrime Ira Mobile Capture</h2>
            <p>
              Instantly capture physical receipts and vendor invoices wherever business takes you. 
              The TallyIra mobile capture interface reads key metrics natively, formats them, 
              and pushes them to your desk installation as verified draft entries.
            </p>
            <ul className="ira-video-feature-list">
              <li>
                <strong>Instant OCR Processing:</strong> Reads values directly off physical papers.
              </li>
              <li>
                <strong>Auto-Draft Sync:</strong> Builds ready-to-post accounting entries on TallyPrime.
              </li>
              <li>
                <strong>Full Owner Approval:</strong> Complete review before final entry posting.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── Key Advantages Grid ── */}
      <section className="ira-advantages-section">
        <div className="section-header">
          <h2>Ultimate Advantages of TallyIra</h2>
          <p>Supercharge your operations with intelligent, secure, and rapid document accounting.</p>
        </div>
        <div className="advantages-grid">
          <div className="adv-card">
            <FaRocket className="adv-icon" />
            <h3>Rapid Turnaround</h3>
            <p>Drastically shorten month-end and year-end book closings by matching transaction logs instantly.</p>
          </div>
          <div className="adv-card">
            <FaLock className="adv-icon" />
            <h3>Enterprise Security</h3>
            <p>Multi-layered user controls mean only validated stakeholders can push AI drafts into actual ledgers.</p>
          </div>
          <div className="adv-card">
            <FaRobot className="adv-icon" />
            <h3>Constant Evolution</h3>
            <p>Machine learning models refine continuously to identify new formats and custom voucher type configurations.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TallyIra;
